import { OUTCOME_ICON, buildingLabel, outcomeLabel, type Report } from '../api/types';
import { formatCoords, formatDate } from '../lib/reportFormat';
import { UnitTable } from './UnitTable';

/**
 * Vollständiger Inhalt eines Berichts, angelehnt an die Aufteilung des
 * echten Spielberichts (Beträff/Kampfzeit oben, Truppentabellen je Seite,
 * Spionage, Schaden unten) -- statt des vom Spiel selbst erzeugten
 * Sieg/Niederlage-Banners (sprachabhängiger Fließtext) steht hier das
 * eigene, aus den Verlustzahlen berechnete Ergebnis samt Icon. Wird als
 * Hover-Popup über dem Namen in der Berichtsliste verwendet.
 */
export function ReportDetails({ report }: { report: Report }) {
  const hasScout = Boolean(
    report.scout?.resources || (report.scout?.buildings && report.scout.buildings.length > 0),
  );
  const hasDamage = Boolean(report.wallDamage || report.catapultDamage || report.loyaltyDamage);
  const hasElsewhere = Boolean(report.defenderUnitsAway || report.defenderUnitsInOtherVillages);
  const luckPercent = report.luck === null ? null : Math.max(0, Math.min(100, report.luck));

  return (
    <div className="report-details">
      <table className="report-details-head">
        <tbody>
          <tr>
            <th>Beträff</th>
            <td>{report.name ?? '—'}</td>
          </tr>
          <tr>
            <th>Kampfzeit</th>
            <td>{formatDate(report.battleAt)}</td>
          </tr>
          {report.viewerOutcome?.outcome && (
            <tr>
              <th>Ergebnis</th>
              <td>
                <img
                  src={OUTCOME_ICON[report.viewerOutcome.outcome]}
                  alt=""
                  className="report-icon"
                />{' '}
                {outcomeLabel(report.viewerOutcome.side, report.viewerOutcome.outcome)}
              </td>
            </tr>
          )}
          {luckPercent !== null && (
            <tr>
              <th>Glück</th>
              <td>
                <span className="luck-bar">
                  <span className="luck-bar-fill" style={{ width: `${luckPercent}%` }} />
                </span>{' '}
                {report.luck} %
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="report-details-side">
        <p className="report-details-side-head">
          Angreifer: {report.attacker.player ?? '—'}{' '}
          <span className="muted">{formatCoords(report.attacker)}</span>
        </p>
        <UnitTable units={report.attacker.units} losses={report.attacker.losses} />
      </div>

      <div className="report-details-side">
        <p className="report-details-side-head">
          Verteidiger: {report.defender.player ?? '—'}{' '}
          <span className="muted">{formatCoords(report.defender)}</span>
        </p>
        <UnitTable units={report.defender.units} losses={report.defender.losses} />
      </div>

      {hasScout && (
        <div className="report-details-section">
          <p className="report-details-subhead">Spionage</p>
          {report.scout?.resources && (
            <p>
              Holz {report.scout.resources.wood.toLocaleString('de-DE')} · Lehm{' '}
              {report.scout.resources.stone.toLocaleString('de-DE')} · Eisen{' '}
              {report.scout.resources.iron.toLocaleString('de-DE')}
            </p>
          )}
          {report.scout?.buildings && report.scout.buildings.length > 0 && (
            <table className="report-details-buildings">
              <tbody>
                {report.scout.buildings.map((building) => (
                  <tr key={building.id}>
                    <td>{buildingLabel(building.id)}</td>
                    <td className="right">{building.level}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {hasDamage && (
        <div className="report-details-section">
          <p className="report-details-subhead">Schaden</p>
          <table className="report-details-head">
            <tbody>
              {report.wallDamage && (
                <tr>
                  <th>Rammböcke</th>
                  <td>
                    Wall: Stufe {report.wallDamage.before} → {report.wallDamage.after}
                  </td>
                </tr>
              )}
              {report.catapultDamage && (
                <tr>
                  <th>Katapultbeschuss</th>
                  <td>
                    {report.catapultDamage.building}: Stufe {report.catapultDamage.before} →{' '}
                    {report.catapultDamage.after}
                  </td>
                </tr>
              )}
              {report.loyaltyDamage && (
                <tr>
                  <th>Zustimmung</th>
                  <td>
                    {report.loyaltyDamage.before} → {report.loyaltyDamage.after}
                    {report.loyaltyDamage.after <= 0 && ' (Dorf erobert)'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {hasElsewhere && (
        <div className="report-details-section">
          <p className="report-details-subhead">Verteidiger andernorts</p>
          {report.defenderUnitsAway && (
            <div className="report-details-side">
              <p className="report-details-side-head">Unterwegs</p>
              <UnitTable units={report.defenderUnitsAway} showLosses={false} />
            </div>
          )}
          {report.defenderUnitsInOtherVillages?.map((entry, index) => (
            <div className="report-details-side" key={`${entry.villageId ?? index}`}>
              <p className="report-details-side-head">
                {entry.village} <span className="muted">{formatCoords(entry)}</span>
              </p>
              <UnitTable units={entry.units} showLosses={false} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
